import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.ts';
import type { Item } from '../lib/types.ts';

interface PlanningProps {
  onOpenItem?: (id: string) => void;
}

type KM = Record<string, string | number | undefined>;

// Match the labels now stored in key_metrics.application_type
const APP_FILTERS: { label: string; match: string }[] = [
  { label: 'All', match: '' },
  { label: 'Permission', match: 'Permission' },
  { label: 'Retention', match: 'Retention' },
  { label: 'Outline', match: 'Outline Permission' },
  { label: 'Extension', match: 'Extension of Duration' },
  { label: 'Amendment', match: 'Significant Further Information' },
];

function getAddress(item: Item): string {
  // Description is newline-separated; address line starts with 📍
  const lines = (item.description ?? '').split('\n');
  const addrLine = lines.find(l => l.startsWith('📍'));
  return addrLine ? addrLine.replace('📍 ', '') : '';
}

function getDescFirstLine(item: Item): string {
  return (item.description ?? '').split('\n')[0] ?? '';
}

export function Planning({ onOpenItem }: PlanningProps) {
  const [items, setItems]   = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [appType, setAppType] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'units'>('date');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Paginate through all records (API cap is 500 per request)
      const PAGE = 500;
      let all: Item[] = [];
      let off = 0;
      while (true) {
        const res = await api.items.list({ source_id: 'planning_apps', limit: PAGE, offset: off });
        all = [...all, ...res.items];
        if (res.items.length < PAGE) break;
        off += PAGE;
      }
      setItems(all);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items
    .filter(item => {
      const km = (item.key_metrics ?? {}) as KM;
      if (appType && String(km['application_type'] ?? '') !== appType) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [item.title, item.description, String(km['applicant'] ?? ''), String(km['planning_authority'] ?? '')].join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'units') {
        const au = Number((a.key_metrics as KM)?.['units_proposed'] ?? 0);
        const bu = Number((b.key_metrics as KM)?.['units_proposed'] ?? 0);
        return bu - au;
      }
      return (b.published_at ?? b.ingested_at) - (a.published_at ?? a.ingested_at);
    });

  const page = filtered.slice(offset, offset + LIMIT);
  const changeFilter = (fn: () => void) => { fn(); setOffset(0); };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Planning Applications</h1>
        <span style={{ color: '#999', fontSize: 13 }}>
          {items.length > 0 ? `${filtered.length} of ${items.length} applications` : ''}
        </span>
        <button onClick={load} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 10px', cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Search applicant, address, description…"
          value={search}
          onChange={e => changeFilter(() => setSearch(e.target.value))}
          style={{ padding: '5px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 4, width: 280 }}
        />
        <span style={{ color: '#ddd' }}>|</span>
        {APP_FILTERS.map(f => (
          <button key={f.label} onClick={() => changeFilter(() => setAppType(f.match))}
            style={filterBtn(appType === f.match)}>
            {f.label}
          </button>
        ))}
        <span style={{ color: '#ddd' }}>|</span>
        <span style={{ fontSize: 12, color: '#555' }}>Sort:</span>
        <button onClick={() => setSortBy('date')} style={filterBtn(sortBy === 'date')}>Date</button>
        <button onClick={() => setSortBy('units')} style={filterBtn(sortBy === 'units')}>Units ↓</button>
      </div>

      {loading && <p style={{ color: '#999' }}>Loading planning applications…</p>}

      {!loading && items.length === 0 && (
        <div style={{ color: '#999', fontStyle: 'italic', padding: '60px 0', textAlign: 'center' }}>
          <p style={{ fontWeight: 500, color: '#666' }}>No planning applications found.</p>
          <p style={{ fontSize: 12 }}>Applications are fetched from Tailte Éireann's ArcGIS service and will appear after the next cron run.</p>
        </div>
      )}

      {!loading && items.length > 0 && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa' }}>
          <p>No applications match these filters.</p>
          <button onClick={() => { setSearch(''); setAppType(''); setOffset(0); }}
            style={{ fontSize: 12, padding: '4px 12px', cursor: 'pointer' }}>Clear filters</button>
        </div>
      )}

      {page.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={th}>Date</th>
                <th style={th}>Applicant</th>
                <th style={th}>Description</th>
                <th style={th}>Address</th>
                <th style={th}>Authority</th>
                <th style={th}>Type</th>
                <th style={{ ...th, textAlign: 'right' }}>Units</th>
                <th style={th}>Status</th>
                <th style={th}>Link</th>
              </tr>
            </thead>
            <tbody>
              {page.map(item => {
                const km = (item.key_metrics ?? {}) as KM;
                const dateStr = item.published_at
                  ? new Date(item.published_at * 1000).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '—';
                const units    = Number(km['units_proposed'] ?? 0);
                const desc     = getDescFirstLine(item);
                const address  = getAddress(item);
                const status   = String(km['status'] ?? '');
                const decision = String(km['decision'] ?? '');
                const hasAppeal = !!(km['appeal_ref']);
                const statusDisplay = decision || status || '—';
                const isGranted = statusDisplay.toLowerCase().includes('granted');
                const isRefused = statusDisplay.toLowerCase().includes('refused');

                return (
                  <tr key={item.id}
                    style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    onClick={() => onOpenItem?.(item.id)}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#666' }}>{dateStr}</td>
                    <td style={{ ...td, fontWeight: 600, maxWidth: 140 }}>{String(km['applicant'] || '—')}</td>
                    <td style={{ ...td, maxWidth: 280 }}>
                      <span title={desc}>{desc.length > 90 ? desc.slice(0, 90) + '…' : desc || '—'}</span>
                      {hasAppeal && (
                        <span style={{ marginLeft: 6, background: '#fef3c7', color: '#92400e', borderRadius: 3, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>APPEAL</span>
                      )}
                    </td>
                    <td style={{ ...td, color: '#555', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {address || '—'}
                    </td>
                    <td style={{ ...td, color: '#555' }}>{String(km['planning_authority'] || '—')}</td>
                    <td style={{ ...td, color: '#555', whiteSpace: 'nowrap' }}>{String(km['application_type'] || '—')}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: units > 0 ? 600 : 400, color: units >= 50 ? '#991b1b' : 'inherit' }}>
                      {units > 0 ? units.toLocaleString() : '—'}
                    </td>
                    <td style={td}>
                      <span style={{
                        background: isGranted ? '#dcfce7' : isRefused ? '#fee2e2' : '#f3f4f6',
                        color: isGranted ? '#166534' : isRefused ? '#991b1b' : '#374151',
                        borderRadius: 3, padding: '2px 6px', fontSize: 11, whiteSpace: 'nowrap',
                      }}>
                        {statusDisplay}
                      </span>
                    </td>
                    <td style={td} onClick={e => e.stopPropagation()}>
                      {item.raw_url
                        ? <a href={item.raw_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 11 }}>View ↗</a>
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > LIMIT && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>← Prev</button>
          <span style={{ fontSize: 12, color: '#666' }}>
            {offset + 1}–{Math.min(offset + LIMIT, filtered.length)} of {filtered.length}
          </span>
          <button disabled={offset + LIMIT >= filtered.length} onClick={() => setOffset(o => o + LIMIT)}>Next →</button>
        </div>
      )}
    </div>
  );
}

function filterBtn(active: boolean): React.CSSProperties {
  return {
    background: active ? '#111' : '#f0f0f0',
    color: active ? '#fff' : '#555',
    border: 'none', borderRadius: 4,
    padding: '4px 10px', fontSize: 12,
    cursor: 'pointer', fontWeight: active ? 700 : 400,
  };
}

const th: React.CSSProperties = {
  padding: '8px 10px', fontWeight: 600, fontSize: 12, color: '#555',
  borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top' };
